import type { CanvasSectionBlock } from '../sectionRegistry';
import {
  type AlignmentAnalysis,
  type AlignmentBox,
  type CandidateRejection,
  serializeAlignmentBox,
} from './alignmentMath';
import {
  resolveAlignmentBoxSource,
  resolveEditorBoundsFromDom,
  resolveEditorBoundsMode,
  resolveLiveActiveBoxFromLayer,
  type EditorBoundsBox,
  type EditorBoundsMode,
} from './editorBounds';
import type { MovementDebugHoverGeometry } from './movementDebug';

export type ChosenBoxSource = 'content' | 'frame' | 'layout';

export type GeometryAuditBox = ReturnType<typeof serializeAlignmentBox>;

export type GeometryAudit = {
  blockId: string;
  blockKind: string;
  rootBox?: GeometryAuditBox;
  frameBox?: GeometryAuditBox;
  contentBox?: GeometryAuditBox;
  activeVisualBox?: GeometryAuditBox;
  editingFrameBox?: GeometryAuditBox;
  alignmentBox?: GeometryAuditBox;
  activeBox?: GeometryAuditBox;
  selectionOverlayBox?: GeometryAuditBox;
  dragStartBox?: GeometryAuditBox;
  previewMovingBox?: GeometryAuditBox;
  committedBox?: GeometryAuditBox;
  boundsMode: EditorBoundsMode;
  chosenBoxSource: ChosenBoxSource;
  coordinateSpace: 'section-layer-px';
  transformsIncluded: true;
  boxSourceReason: string;
};

export type CandidateGeometryEntry = {
  blockId: string;
  blockKind: string;
  rootBox?: GeometryAuditBox;
  frameBox?: GeometryAuditBox;
  contentBox?: GeometryAuditBox;
  activeVisualBox?: GeometryAuditBox;
  editingFrameBox?: GeometryAuditBox;
  activeBox?: GeometryAuditBox;
  boundsMode: EditorBoundsMode;
  chosenBoxSource: ChosenBoxSource;
  includedForAlignment: boolean;
  includedForGap: boolean;
  rejectionReason?: string;
};

export function resolveBoxSourceReason(block: CanvasSectionBlock): string {
  const mode = resolveEditorBoundsMode(block);
  if (mode === 'content') {
    return 'Visual active box uses measured text/content bounds from [data-builder-editor-bounds="content"]';
  }
  if (mode === 'text-paragraph') {
    return 'Visual alignment uses content bounds; editingFrameBox keeps grid frame width for paragraph wrapping/editing';
  }
  return 'Visual active box uses block root [data-builder-block-root]';
}

export function resolveChosenBoxSource(
  block: CanvasSectionBlock,
  bounds?: EditorBoundsBox | null
): ChosenBoxSource {
  return resolveAlignmentBoxSource(block, bounds);
}

export function measureEditorBoundsForBlock(
  block: CanvasSectionBlock,
  layer: HTMLElement,
  colWidth?: number,
  rowHeight?: number
): EditorBoundsBox | null {
  return resolveEditorBoundsFromDom(block, layer, { colWidth, rowHeight });
}

function optionalSerializedBox(box?: AlignmentBox | null): GeometryAuditBox | undefined {
  if (!box) return undefined;
  return serializeAlignmentBox(box);
}

function serializeBoundsAuditFields(bounds: EditorBoundsBox | undefined) {
  const activeVisual = bounds?.activeVisualBox ?? bounds?.activeBox;
  return {
    rootBox: optionalSerializedBox(bounds?.frameBox),
    frameBox: optionalSerializedBox(bounds?.frameBox),
    contentBox: optionalSerializedBox(bounds?.contentBox),
    activeVisualBox: optionalSerializedBox(activeVisual),
    editingFrameBox: optionalSerializedBox(bounds?.editingFrameBox),
    alignmentBox: optionalSerializedBox(activeVisual),
    activeBox: optionalSerializedBox(activeVisual),
  };
}

export function buildGeometryAudit({
  block,
  layer,
  colWidth,
  rowHeight,
  editorBounds,
  dragStartBox,
  previewMovingBox,
  committedBox,
}: {
  block: CanvasSectionBlock;
  layer: HTMLElement | null;
  colWidth: number;
  rowHeight: number;
  editorBounds?: EditorBoundsBox;
  dragStartBox?: AlignmentBox;
  previewMovingBox?: AlignmentBox;
  committedBox?: AlignmentBox;
}): GeometryAudit {
  const liveBounds =
    layer ? measureEditorBoundsForBlock(block, layer, colWidth, rowHeight) : null;
  const bounds = liveBounds ?? editorBounds;
  const boundsMode = bounds?.mode ?? resolveEditorBoundsMode(block);
  const chosenBoxSource = resolveChosenBoxSource(block, bounds);
  const serialized = serializeBoundsAuditFields(bounds);

  return {
    blockId: block.id,
    blockKind: block.type,
    ...serialized,
    boundsMode,
    selectionOverlayBox: serialized.activeVisualBox,
    dragStartBox: optionalSerializedBox(dragStartBox),
    previewMovingBox: optionalSerializedBox(previewMovingBox),
    committedBox: optionalSerializedBox(committedBox),
    chosenBoxSource,
    coordinateSpace: 'section-layer-px',
    transformsIncluded: true,
    boxSourceReason: resolveBoxSourceReason(block),
  };
}

export function buildCandidateGeometry({
  candidates,
  movingBlockId,
  layer,
  colWidth,
  rowHeight,
  editorBoundsByBlockId,
  candidateRejections = [],
  rowPeerIds = new Set<string>(),
  gapNeighborIds = new Set<string>(),
}: {
  candidates: CanvasSectionBlock[];
  movingBlockId: string;
  layer: HTMLElement | null;
  colWidth: number;
  rowHeight: number;
  editorBoundsByBlockId: Record<string, EditorBoundsBox>;
  candidateRejections?: CandidateRejection[];
  rowPeerIds?: Set<string>;
  gapNeighborIds?: Set<string>;
}): CandidateGeometryEntry[] {
  const rejectionByBlockId = new Map(
    candidateRejections.map((entry) => [entry.blockId, entry.reason])
  );

  return candidates.map((block) => {
    const registryBounds = editorBoundsByBlockId[block.id];
    const liveBounds =
      layer ? measureEditorBoundsForBlock(block, layer, colWidth, rowHeight) : null;
    const bounds = liveBounds ?? registryBounds;
    const boundsMode = bounds?.mode ?? resolveEditorBoundsMode(block);
    const chosenBoxSource = resolveChosenBoxSource(block, bounds);
    const rejectionReason = rejectionByBlockId.get(block.id);
    const includedForAlignment = block.id !== movingBlockId;
    const includedForGap =
      gapNeighborIds.has(block.id) ||
      (rowPeerIds.has(block.id) && !rejectionReason);
    const activeVisual = bounds?.activeVisualBox ?? bounds?.activeBox;

    return {
      blockId: block.id,
      blockKind: block.type,
      rootBox: optionalSerializedBox(bounds?.frameBox),
      frameBox: optionalSerializedBox(bounds?.frameBox),
      contentBox: optionalSerializedBox(bounds?.contentBox),
      activeVisualBox: optionalSerializedBox(activeVisual),
      editingFrameBox: optionalSerializedBox(bounds?.editingFrameBox),
      activeBox: optionalSerializedBox(activeVisual),
      boundsMode,
      chosenBoxSource,
      includedForAlignment,
      includedForGap,
      rejectionReason,
    };
  });
}

export function resolveLiveActiveBoxForBlock(
  layer: HTMLElement,
  block: CanvasSectionBlock
): AlignmentBox | null {
  return resolveLiveActiveBoxFromLayer(layer, block.id, block);
}

export function collectGapNeighborIds(gapGuides: Array<{ neighbourBlockId?: string }>) {
  const ids = new Set<string>();
  gapGuides.forEach((guide) => {
    if (guide.neighbourBlockId) ids.add(guide.neighbourBlockId);
  });
  return ids;
}

export function buildHoverGeometryDebug({
  block,
  bounds,
  isSelected,
}: {
  block: CanvasSectionBlock;
  bounds?: EditorBoundsBox | null;
  isSelected: boolean;
}): MovementDebugHoverGeometry {
  const activeVisual = bounds?.activeVisualBox ?? bounds?.activeBox;
  const hitTest = bounds?.frameBox;
  const chosenBoxSource = resolveChosenBoxSource(block, bounds);
  const hoverVisualBox = activeVisual ? serializeAlignmentBox(activeVisual) : undefined;
  const hitTestBox = hitTest ? serializeAlignmentBox(hitTest) : undefined;
  const boxesMatch =
    hoverVisualBox &&
    hitTestBox &&
    Math.abs(hoverVisualBox.width - hitTestBox.width) <= 0.5 &&
    Math.abs(hoverVisualBox.height - hitTestBox.height) <= 0.5;

  return {
    blockId: block.id,
    hoverVisualBox,
    hitTestBox,
    selectedVisualBox: isSelected ? hoverVisualBox : undefined,
    chosenBoxSource,
    hoverUsesActiveVisualBox: Boolean(
      hoverVisualBox &&
        hitTestBox &&
        (Math.abs(hoverVisualBox.left - hitTestBox.left) > 0.5 ||
          Math.abs(hoverVisualBox.top - hitTestBox.top) > 0.5 ||
          Math.abs(hoverVisualBox.width - hitTestBox.width) > 0.5 ||
          Math.abs(hoverVisualBox.height - hitTestBox.height) > 0.5)
    ),
  };
}

export function buildMovementGeometryPayload({
  block,
  blocks,
  layer,
  colWidth,
  rowHeight,
  editorBoundsByBlockId,
  dragStartBox,
  previewMovingBox,
  committedBox,
  analysis,
}: {
  block: CanvasSectionBlock;
  blocks: CanvasSectionBlock[];
  layer: HTMLElement | null;
  colWidth: number;
  rowHeight: number;
  editorBoundsByBlockId: Record<string, EditorBoundsBox>;
  dragStartBox?: AlignmentBox;
  previewMovingBox?: AlignmentBox;
  committedBox?: AlignmentBox;
  analysis?: AlignmentAnalysis;
}): {
  geometryAudit: GeometryAudit;
  candidateGeometry: CandidateGeometryEntry[];
} {
  const editorBounds = editorBoundsByBlockId[block.id];
  const gapNeighborIds = analysis ? collectGapNeighborIds(analysis.gapGuides) : new Set<string>();
  const rowPeerIds = new Set(analysis?.rowPeerIds ?? []);

  return {
    geometryAudit: buildGeometryAudit({
      block,
      layer,
      colWidth,
      rowHeight,
      editorBounds,
      dragStartBox,
      previewMovingBox,
      committedBox,
    }),
    candidateGeometry: buildCandidateGeometry({
      candidates: blocks.filter((candidate) => candidate.id !== block.id),
      movingBlockId: block.id,
      layer,
      colWidth,
      rowHeight,
      editorBoundsByBlockId,
      candidateRejections: analysis?.candidateRejections,
      rowPeerIds,
      gapNeighborIds,
    }),
  };
}
